/** Mix candidate mic + AI TTS into one recording stream, with higher video bitrate. */

export function pickRecorderMime() {
  if (!window.MediaRecorder) return '';
  const types = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=h264,opus',
    'video/webm',
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4',
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export function videoBitrateForTrack(track) {
  const s = track?.getSettings?.() || {};
  const pixels = (s.width || 1280) * (s.height || 720);
  if (pixels >= 1800 * 900) return 5_500_000;
  if (pixels >= 1200 * 700) return 3_800_000;
  return 2_200_000;
}

export function createRecorder(stream, mime) {
  const videoTrack = stream.getVideoTracks()[0];
  const attempts = [
    {
      mimeType: mime,
      videoBitsPerSecond: videoBitrateForTrack(videoTrack),
      audioBitsPerSecond: 192_000,
    },
    {
      mimeType: mime,
      videoBitsPerSecond: 2_500_000,
      audioBitsPerSecond: 128_000,
    },
    { mimeType: mime },
    {},
  ];
  let lastErr;
  for (const opts of attempts) {
    try {
      const clean = { ...opts };
      if (!clean.mimeType) delete clean.mimeType;
      if (clean.mimeType && !MediaRecorder.isTypeSupported(clean.mimeType)) continue;
      return new MediaRecorder(stream, clean);
    } catch (err) {
      lastErr = err;
    }
  }
  if (lastErr) throw lastErr;
  return new MediaRecorder(stream);
}

/**
 * Routes mic into the recording only (not speakers) and AI TTS into
 * both speakers and the recording, so HR hears the full conversation.
 */
export function createInterviewMixer(liveStream) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx || !liveStream) return null;

  let ctx;
  let dest;
  let micSource;
  let micGain;
  let ttsGain;
  let readyPromise;
  let stopped = false;

  const start = () => {
    if (ctx || stopped) return;
    ctx = new AudioCtx();
    dest = ctx.createMediaStreamDestination();
    ttsGain = ctx.createGain();
    ttsGain.gain.value = 1;
    ttsGain.connect(dest);
    ttsGain.connect(ctx.destination);

    try {
      micSource = ctx.createMediaStreamSource(liveStream);
      micGain = ctx.createGain();
      micGain.gain.value = 1;
      micSource.connect(micGain);
      micGain.connect(dest);
    } catch (_) {
      micSource = null;
      micGain = null;
    }

    readyPromise = ctx.state === 'suspended' ? ctx.resume().catch(() => {}) : Promise.resolve();
  };

  const ready = async () => {
    start();
    await readyPromise;
    if (ctx?.state === 'suspended') await ctx.resume().catch(() => {});
  };

  const recordStream = () => {
    start();
    const videos = (liveStream.getVideoTracks() || []).filter((t) => t.readyState === 'live');
    const mixedAudio = dest?.stream?.getAudioTracks?.() || [];
    if (!videos.length && !mixedAudio.length) return liveStream;
    return new MediaStream([...videos, ...mixedAudio]);
  };

  const playTts = async (blob) => {
    if (!blob || blob.size < 40) return false;
    await ready();
    if (micGain) micGain.gain.value = 0.2;
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    audio.src = url;
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    let elementSource = null;
    try {
      try {
        elementSource = ctx.createMediaElementSource(audio);
        elementSource.connect(ttsGain);
      } catch (_) {
        await new Promise((resolve) => {
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play().catch(resolve);
        });
        return false;
      }
      await new Promise((resolve) => {
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
      return true;
    } finally {
      if (micGain) micGain.gain.value = 1;
      try { elementSource?.disconnect(); } catch (_) { /* ignore */ }
      URL.revokeObjectURL(url);
    }
  };

  const stop = () => {
    stopped = true;
    try { micSource?.disconnect(); } catch (_) { /* ignore */ }
    try { ttsGain?.disconnect(); } catch (_) { /* ignore */ }
    if (ctx && ctx.state !== 'closed') ctx.close().catch(() => {});
    ctx = null;
    dest = null;
    micSource = null;
    ttsGain = null;
  };

  return { start, ready, recordStream, playTts, stop };
}
